import React, { useState } from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { processSteps } from '../../mock';
import ContactPopup from '../../components/ContactPopup';

const PatentApplication = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-950">
      <section className="relative bg-gradient-to-b from-slate-900 to-slate-950 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center bg-amber-500/10 border border-white/20 rounded-full px-4 py-2 mb-6">
              <span className="text-sm text-amber-500 font-medium">Patent Services</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Patent Application</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Professional patent application preparation and filing services. From provisional to utility patents, we help inventors protect their innovations.
            </p>
            <Button 
              size="lg" 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-xl transform transition-transform hover:scale-105"
            >
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Key Benefits</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div key="0" className="flex items-start space-x-3 bg-slate-900/40 border border-white/20 rounded-lg p-4 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all">
              <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-300">Professional patent drafting</span>
            </div>
            <div key="1" className="flex items-start space-x-3 bg-slate-900/40 border border-white/20 rounded-lg p-4 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all">
              <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-300">Detailed technical drawings</span>
            </div>
            <div key="2" className="flex items-start space-x-3 bg-slate-900/40 border border-white/20 rounded-lg p-4 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all">
              <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-300">Comprehensive claim sets</span>
            </div>
            <div key="3" className="flex items-start space-x-3 bg-slate-900/40 border border-white/20 rounded-lg p-4 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all">
              <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-300">Provisional and utility applications</span>
            </div>
            <div key="4" className="flex items-start space-x-3 bg-slate-900/40 border border-white/20 rounded-lg p-4 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all">
              <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-300">Patent Cooperation Treaty (PCT) filings</span>
            </div>
            <div key="5" className="flex items-start space-x-3 bg-slate-900/40 border border-white/20 rounded-lg p-4 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all">
              <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-300">Strategic claim drafting</span>
            </div>
            <div key="6" className="flex items-start space-x-3 bg-slate-900/40 border border-white/20 rounded-lg p-4 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all">
              <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-300">Prior art analysis inclusion</span>
            </div>
            <div key="7" className="flex items-start space-x-3 bg-slate-900/40 border border-white/20 rounded-lg p-4 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all">
              <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-300">Maximize patent protection scope</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Streamlined Process</h2>
          </div>
          <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((step) => (
              <div key={step.step} className="relative">
                <div className="bg-slate-900 border border-white/20 rounded-xl p-8 h-full hover:border-white/40 hover:shadow-xl hover:shadow-white/10 hover:scale-[1.02] transition-all group">
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white w-16 h-16 rounded-full flex items-center justify-center font-black text-2xl mb-6 shadow-xl group-hover:scale-110 transition-transform">
                    {step.step}
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">{step.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="md:hidden overflow-x-auto scrollbar-hide">
            <div className="flex gap-4 pb-4" style={{scrollSnapType:'x mandatory'}}>
              {processSteps.map((step) => (
                <div key={step.step} className="flex-shrink-0 w-[300px]" style={{scrollSnapAlign:'center'}}>
                  <div className="bg-slate-900 border border-white/20 rounded-xl p-6 h-full">
                    <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white w-14 h-14 rounded-full flex items-center justify-center font-black text-xl mb-4 shadow-xl">
                      {step.step}
                    </div>
                    <h3 className="text-white font-bold text-lg mb-3">{step.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-white/20 rounded-2xl p-12 shadow-2xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Let our experts help you with professional patent application services.
            </p>
            <Button 
              size="lg" 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-xl transform transition-transform hover:scale-105"
            >
              Get Started Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default PatentApplication;
